import {
  CheckCircle,
  Close,
  ContactMail,
  Delete,
  Edit,
  EditNote,
  GpsFixed,
  MoreVert,
  RadioButtonUnchecked,
  RefreshOutlined,
  Search,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Company, Employee } from "../../types";
import { hostname } from "../../utils";
import SenderProfileEditorDialog from "./SenderProfileEditorDialog";
import BulkEditSenderProfilesDialog from "./dialogs/BulkEditSenderProfilesDialog";
import TrackingDomainsDialog from "./dialogs/TrackingDomainsDialog";

export default ({
  open,
  onClose,
  companyId,
  eventListener,
  getToken,
  employees,
  company,
}: {
  open: boolean;
  onClose: () => void;
  companyId: string;
  eventListener: Function;
  getToken: Function;
  employees: Employee[];
  company: Company;
}) => {
  const [profiles, setProfiles] = useState<Array<any>>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<any>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuProfile, setSelectedMenuProfile] = useState<any>(null);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [trackingDomainsOpen, setTrackingDomainsOpen] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleClose = () => {
    onClose();
  };

  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0); // Reset to first page on new search
    }, 300);
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const refreshProfiles = async () => {
    setRefreshing(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/sequences/profiles`, {
        params: { companyId, page: page + 1, pageSize, search: search || undefined },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setProfiles(res.data.items);
        setTotal(res.data.total);
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const loadProfiles = async () => {
    setLoading(true);
    await refreshProfiles();
    await getIntegrations();
    setLoading(false);
  };

  const getIntegrations = async () => {
    const currentToken = await getToken();
    try {
      const res = await axios.get(`${hostname}/integrations/by-type/email`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setIntegrations(res.data.items || []);
    } catch (error) {
      setIntegrations([]);
    }
  };

  const handleDelete = async () => {
    if (!profileToDelete) return;

    const currentToken = await getToken();
    await axios.delete(`${hostname}/sequences/profiles/${profileToDelete.id}`, {
      params: { companyId },
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    setDeleteDialogOpen(false);
    setProfileToDelete(null);
    refreshProfiles();
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    profile: any
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedMenuProfile(profile);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMenuProfile(null);
  };

  const handleEdit = () => {
    setSelectedProfile(selectedMenuProfile);
    setEditorOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setProfileToDelete(selectedMenuProfile);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedProfiles(new Set(profiles.map((p) => p.id)));
    } else {
      setSelectedProfiles(new Set());
    }
  };

  const handleSelectProfile = (profileId: string) => {
    const newSelected = new Set(selectedProfiles);
    if (newSelected.has(profileId)) {
      newSelected.delete(profileId);
    } else {
      newSelected.add(profileId);
    }
    setSelectedProfiles(newSelected);
  };

  const handleBulkEditSuccess = () => {
    setSelectedProfiles(new Set());
    refreshProfiles();
  };

  // Initial load when dialog opens
  useEffect(() => {
    if (open) {
      loadProfiles();
      setSelectedProfiles(new Set());
      setSearchInput("");
      setSearch("");
    }
  }, [open]);

  // Refresh when pagination or search changes
  useEffect(() => {
    if (open) {
      refreshProfiles();
    }
  }, [page, pageSize, search]);

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography variant={"h6"}>Sender Profiles</Typography>
          <IconButton onClick={() => handleClose()}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Paper style={{ marginTop: 20, marginBottom: 40 }} variant="outlined">
          {refreshing && <LinearProgress />}
          {!refreshing && <div style={{ height: 4 }}></div>}
          <Button
            sx={{ m: 1 }}
            variant="contained"
            onClick={() => {
              setSelectedProfile(null);
              setEditorOpen(true);
            }}
            startIcon={<ContactMail />}
          >
            Create Profile
          </Button>
          {selectedProfiles.size > 0 && (
            <Button
              sx={{ m: 1 }}
              variant={"outlined"}
              onClick={() => setBulkEditOpen(true)}
              startIcon={<EditNote />}
            >
              Bulk Edit ({selectedProfiles.size})
            </Button>
          )}
          <Button
            sx={{ m: 1 }}
            variant={"outlined"}
            onClick={() => setTrackingDomainsOpen(true)}
            startIcon={<GpsFixed />}
          >
            Tracking Domains
          </Button>
          <Box sx={{ float: "right", display: "flex", alignItems: "center", mr: 1 }}>
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ width: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton onClick={() => refreshProfiles()}>
              <RefreshOutlined />
            </IconButton>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedProfiles.size > 0 &&
                        selectedProfiles.size < profiles.length
                      }
                      checked={
                        profiles.length > 0 &&
                        selectedProfiles.size === profiles.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell width="60"></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Email Account</TableCell>
                  <TableCell align="center">LinkedIn Account</TableCell>
                  <TableCell align="center">Phone Account</TableCell>
                  <TableCell align="center">Agent</TableCell>
                  <TableCell align="center">Voice</TableCell>
                  <TableCell align="center">Tracking Domain</TableCell>
                  <TableCell align="center">Signature</TableCell>
                  <TableCell align="center">Daily Email Limit</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile) => {
                  const employee = profile.employeeId
                    ? employees.find((emp) => emp.id === profile.employeeId)
                    : null;

                  return (
                    <TableRow
                      key={profile.id}
                      hover
                      selected={selectedProfiles.has(profile.id)}
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setSelectedProfile(profile);
                        setEditorOpen(true);
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedProfiles.has(profile.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleSelectProfile(profile.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          src={employee?.picture || ""}
                          sx={{ width: 40, height: 40 }}
                        >
                          {!employee?.picture &&
                            profile.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                      </TableCell>
                      <TableCell>{profile.name}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{profile.email}</Typography>
                          {profile.warmupStats && (() => {
                            const healthScore = profile.warmupStats.health_score_label || "0%";
                            const healthValue = parseInt(healthScore);
                            return (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={`Health: ${healthScore}`}
                                  size="small"
                                  color={
                                    healthValue >= 80
                                      ? "success"
                                      : healthValue >= 50
                                      ? "warning"
                                      : "error"
                                  }
                                  sx={{ fontWeight: "bold", height: 20, fontSize: "0.7rem" }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.7rem" }}
                                >
                                  Sent: {profile.warmupStats.sent || 0} |
                                  Inbox: {profile.warmupStats.landed_inbox || 0} |
                                  Received: {profile.warmupStats.received || 0}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.integrationId
                              ? "Email account connected"
                              : "No email account"
                          }
                        >
                          {profile.integrationId ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.linkedinIntegrationId
                              ? "LinkedIn account connected"
                              : "No LinkedIn account"
                          }
                        >
                          {profile.linkedinIntegrationId ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.phoneIntegrationId
                              ? "Phone account connected"
                              : "No phone account"
                          }
                        >
                          {profile.phoneIntegrationId ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.agentId
                              ? "Agent configured"
                              : "No agent"
                          }
                        >
                          {profile.agentId ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.voiceId
                              ? "Voice configured"
                              : "No voice"
                          }
                        >
                          {profile.voiceId ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.trackingDomainId
                              ? "Custom tracking domain configured"
                              : "Using default tracking domain"
                          }
                        >
                          {profile.trackingDomainId ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            profile.signature
                              ? "Signature configured"
                              : "No signature"
                          }
                        >
                          {profile.signature ? (
                            <CheckCircle color="primary" fontSize="small" />
                          ) : (
                            <RadioButtonUnchecked
                              color="disabled"
                              fontSize="small"
                            />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        {profile.dailySendingLimit || 15}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleMenuOpen(event, profile);
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Paper>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Profile</ListItemText>
          </MenuItem>
        </Menu>
      </DialogContent>
      <SenderProfileEditorDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setSelectedProfile(null);
          refreshProfiles();
        }}
        profile={selectedProfile}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
        employees={employees}
        company={company}
        integrations={integrations}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setProfileToDelete(null);
        }}
      >
        <DialogTitle>Delete Sender Profile</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the sender profile "
            {profileToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setProfileToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tracking Domains Dialog */}
      <TrackingDomainsDialog
        open={trackingDomainsOpen}
        onClose={() => setTrackingDomainsOpen(false)}
        companyId={companyId}
        getToken={getToken as () => Promise<string>}
      />

      {/* Bulk Edit Dialog */}
      <BulkEditSenderProfilesDialog
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedCount={selectedProfiles.size}
        selectedIds={Array.from(selectedProfiles)}
        companyId={companyId}
        getToken={getToken as () => Promise<string>}
        onSuccess={handleBulkEditSuccess}
      />
    </Dialog>
  );
};
